import React, { useEffect, useState } from "react";
import {
  Typography,
  Grid,
  Button,
  Box,
  Paper,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
  Card,
  IconButton,
  Popper,
  ClickAwayListener,
  TextField,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import { IconTableExport } from "@tabler/icons-react";
import axios from "axios";
import { convertUtcToIST } from "utils/ISTConvert";
import handleExport from "utils/exportData";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDAutocomplete from "components/MDAutocomplete";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDTextField from "components/MDTextField";
import Footer from "examples/Footer";
import { useQuery } from "@tanstack/react-query";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import { addDays, format } from "date-fns";
import { enUS } from "date-fns/locale";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const AttendanceDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user_id, start_date, end_date } = location.state || {};

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [message, setMessage] = useState({ status: "", title: "", msg: "" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState([
    {
      startDate: addDays(new Date(), -7),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [rowSelection, setRowSelection] = useState({});
  const [exportData, setExportData] = useState([]);
  const [showNote, setShowNote] = useState(true);

  const [filters, setFilters] = useState({
    user_name: location.state?.user_id || "",
    district_name: "",
    start_date: null,
    end_date: null,
    status: "",
    date_range: null,
  });
  const attendanceOptions = [
    { title: "Present", value: "Present" },
    { title: "Present (On Duty)", value: "Present (On Duty)" },
    { title: "Leave", value: "Leave" },
    { title: "Absent", value: "Absent" },
    { title: "Holiday", value: "Holiday" },
    { title: "Holiday Attendance", value: "Holiday Attendance" },
  ];

  const leaveOptions = [
    { label: "Sick Leave", value: "Sick Leave" },
    { label: "Casual Leave", value: "Casual Leave" },
    // { label: "Paid Leave", value: "Paid Leave" },
    { label: "On Duty", value: "On Duty" },
  ];

  const fetchDistrict = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/districts/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: districtData,
    error: districtError,
    isLoading: districtIsLoading,
  } = useQuery({
    queryKey: ["fetchDistrict"],
    queryFn: fetchDistrict,
    retry: false,
  });

  const fetchUsers = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/user/role3`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: usersData,
    error: usersError,
    isLoading: usersIsLoading,
  } = useQuery({
    queryKey: ["fetchUsers"],
    queryFn: fetchUsers,
    retry: false,
  });

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  const fetchAttendanceData = async (
    pageIndex = 0,
    pageSize = 10,
    filterValues = filters,
    sortValues = sorting,
    columnFilters = []
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pageIndex + 1,
        limit: pageSize,
        columnFilters: JSON.stringify(columnFilters),
      });

      // If no global filters applied, then use default user_id
      const isFiltered =
        !!filterValues.user_name ||
        !!filterValues.district_name ||
        !!filterValues.status ||
        !!filterValues.start_date ||
        !!filterValues.end_date;

      const hasUserName = !!String(filterValues.user_name || "").trim();
      const hasDistrict = !!String(filterValues.district_name || "").trim();
      const hasDateOrStatus =
        !!String(filterValues.start_date || "").trim() ||
        !!String(filterValues.end_date || "").trim() ||
        !!String(filterValues.status || "").trim();

      if (hasUserName) {
        queryParams.append("user_name", String(filterValues.user_name).trim());
      } else if (!hasDistrict && (hasDateOrStatus || !hasUserName)) {
        queryParams.append("user_id", user_id);
      }

      // Add filters
      // if (filterValues.user_name) queryParams.append("user_name", filterValues.user_name);
      if (filterValues.district_name)
        queryParams.append("district_name", filterValues.district_name);
      if (filterValues.status) queryParams.append("status", filterValues.status);

      // Date range: from filters or fallback
      if (filterValues.start_date) {
        queryParams.append("start_date", filterValues.start_date);
      } else if (start_date) {
        queryParams.append("start_date", start_date);
      }

      if (filterValues.end_date) {
        queryParams.append("end_date", filterValues.end_date);
      } else if (end_date) {
        queryParams.append("end_date", end_date);
      }

      // Sorting
      if (sortValues.length > 0) {
        const sortField = sortValues[0].id;
        const sortOrder = sortValues[0].desc ? "desc" : "asc";
        queryParams.append("sortField", sortField);
        queryParams.append("sortOrder", sortOrder);
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/attendance/attendance-data?${queryParams.toString()}`,
        { withCredentials: true }
      );

      if (res.data.status === 200) {
        setAttendanceData(res.data.data);
        setRowCount(res.data.total || 0);
        setExportData(res.data.data);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceData(pagination.pageIndex, pagination.pageSize, filters, sorting, columnFilters);
  }, [pagination.pageIndex, pagination.pageSize, sorting, columnFilters]);

  const columns = [
    {
      accessorKey: "attendance_date",
      header: "Attendance Date",
      Cell: ({ row }) => convertUtcToIST(row.original.attendance_date) ?? "-",
      // eslint-disable-next-line react/prop-types
      Filter: ({ column }) => {
        const [open, setOpen] = useState(false);
        // eslint-disable-next-line react/prop-types
        const filterValue = column.getFilterValue();
        const value = filterValue ? dayjs(filterValue, "DD-MM-YYYY") : null;

        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={value}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              onChange={(newValue) => {
                // eslint-disable-next-line react/prop-types
                column.setFilterValue(newValue ? dayjs(newValue).format("DD-MM-YYYY") : undefined);
              }}
              slotProps={{
                textField: {
                  onClick: () => setOpen(true),
                  variant: "standard",
                  placeholder: "Filter Date",
                  InputProps: {
                    endAdornment: (
                      <React.Fragment>
                        {value && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              // eslint-disable-next-line react/prop-types
                              column.setFilterValue(undefined);
                            }}
                          >
                            <CloseIcon fontSize="small" sx={{ color: "#fff" }} />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => setOpen(true)}>
                          <CalendarTodayIcon fontSize="small" sx={{ color: "#fff" }} />
                        </IconButton>
                      </React.Fragment>
                    ),
                  },
                  sx: {
                    minWidth: "120px",
                    "& .MuiInputBase-root": {
                      color: "#fff",
                      fontSize: "12px",
                      cursor: "pointer",
                    },
                    "& .MuiInputBase-input": {
                      color: "#fff !important",
                      fontSize: "13px",
                      cursor: "pointer",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#fff",
                    },
                    "& .MuiInput-underline:before": {
                      borderBottomColor: "#e0e0e0",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#fff",
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      filterVariant: "autocomplete",
      filterSelectOptions: attendanceOptions.map((opt) => ({ label: opt.title, value: opt.value })),
      Cell: ({ row }) => (
        <Chip
          label={row.original.status}
          color={
            row.original.status === "Present" || row.original.status === "Present (On Duty)"
              ? "success"
              : row.original.status === "Leave"
                ? "warning"
                : row.original.status === "Holiday"
                  ? "info"
                  : row.original.status === "Holiday Attendance"
                    ? "warning"
                    : "error"
          }
          sx={{ fontWeight: "bold", color: "white" }}
        />
      ),
    },
    {
      accessorKey: "leave_type",
      header: "Leave Type",
      filterVariant: "autocomplete",
      filterSelectOptions: leaveOptions,
      Cell: ({ row }) => (row.original.leave_type === "On Duty" ? "-" : row.original.leave_type) || "-",
    },
    {
      accessorKey: "is_additional_holiday",
      header: "Holiday Attendance",
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const isAdd = row.original.is_additional_holiday;
        const isStat = row.original.status === "Holiday Attendance";
        return isAdd === 1 || isStat ? (
          <Chip label="Yes" color="info" sx={{ fontWeight: "bold", color: "white" }} />
        ) : (
          "-"
        );
      },
    },
    {
      accessorKey: "logIn_time",
      header: "Check-In Time",
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const time = row.original.logIn_time;
        if (!time) return "-";
        const [hour, minute] = time.split(":");
        const h = parseInt(hour);
        const suffix = h >= 12 ? "PM" : "AM";
        return `${((h + 11) % 12) + 1}:${minute} ${suffix}`;
      },
    },
    {
      accessorKey: "logOut_time",
      header: "Check-Out Time",
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const time = row.original.logOut_time;
        if (!time) return "-";
        const [hour, minute] = time.split(":");
        const h = parseInt(hour);
        const suffix = h >= 12 ? "PM" : "AM";
        return `${((h + 11) % 12) + 1}:${minute} ${suffix}`;
      },
    },
    {
      accessorKey: "geotag_img",
      header: "Attendance Image",
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        if (row.original.geotag_img) {
          return (
            <MDBox
              component="img"
              src={row.original.geotag_img}
              alt="Geo-tagged"
              sx={{
                width: "45px",
                height: "45px",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(2.5)",
                  zIndex: 10,
                  position: "relative",
                },
              }}
              onClick={() => window.open(row.original.geotag_img, "_blank")}
            />
          );
        }

        if (row.original.documents) {
          try {
            const docs = JSON.parse(row.original.documents);
            if (Array.isArray(docs) && docs.length > 0) {
              return (
                <MDBox display="flex" flexDirection="column" gap={0.5}>
                  {docs.map((url, idx) => (
                    <MDTypography
                      key={idx}
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#1a73e8",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "bold",
                        "&:hover": { color: "#1557b0" },
                      }}
                    >
                      📄 Doc {idx + 1}
                    </MDTypography>
                  ))}
                </MDBox>
              );
            }
          } catch (e) {
            // ignore
          }
        }

        return "-";
      },
    },
  ];

  const handleExportData = async () => {
    try {
      let formattedData = [];
      if (rowSelection && Object.keys(rowSelection).length > 0) {
        formattedData = exportData.filter((item) => rowSelection[item.id]);
      } else {
        const queryParams = new URLSearchParams({
          page: 1,
          limit: 10000,
        });

        const hasUserName = !!String(filters.user_name || "").trim();
        const hasDistrict = !!String(filters.district_name || "").trim();
        const hasDateOrStatus =
          !!String(filters.start_date || "").trim() ||
          !!String(filters.end_date || "").trim() ||
          !!String(filters.status || "").trim();

        if (hasUserName) {
          queryParams.append("user_name", String(filters.user_name).trim());
        } else if (!hasDistrict && (hasDateOrStatus || !hasUserName)) {
          queryParams.append("user_id", user_id);
        }

        // Add filters
        // if (filterValues.user_name) queryParams.append("user_name", filterValues.user_name);
        if (filters.district_name) queryParams.append("district_name", filters.district_name);
        if (filters.status) queryParams.append("status", filters.status);

        // Date range: from filters or fallback
        if (filters.start_date) {
          queryParams.append("start_date", filters.start_date);
        } else if (start_date) {
          queryParams.append("start_date", start_date);
        }

        if (filters.end_date) {
          queryParams.append("end_date", filters.end_date);
        } else if (end_date) {
          queryParams.append("end_date", end_date);
        }

        // Sorting
        if (sorting.length > 0) {
          const sortField = sorting[0].id;
          const sortOrder = sorting[0].desc ? "desc" : "asc";
          queryParams.append("sortField", sortField);
          queryParams.append("sortOrder", sortOrder);
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/attendance/attendance-data?${queryParams.toString()}`,
          { withCredentials: true }
        );

        if (res.data.status === 200) {
          formattedData = res.data.data;
        }
      }

      if (formattedData.length === 0) {
        console.log("No data found to export");
        setMessage({
          status: "error",
          title: "Error",
          msg: "No data found to export.",
        });
        return;
      }
      await handleExport(formattedData, "Attendance_List_User_Data");
      setMessage({
        status: "success",
        title: "Success",
        msg: "Excel downloaded successfully!",
      });
    } catch (error) {
      console.error(" Export error:", error);
      setMessage({
        status: "error",
        title: "Error",
        msg: "Something went wrong during export.",
      });
    }
  };

  if (meData && meData.role_id === 3) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
      >
        <Typography variant="h4" color="error">
          403 - Unauthorized
        </Typography>
        <Typography>You are not authorized to access this page.</Typography>
      </Box>
    );
  }

  return (
    <DashboardLayout>
      <MDBox mt={3} />
      <Card id="delete-account" sx={{ mb: 2 }}>
        <MDBox pt={2} px={2}>
          {/* <MDBox mt={5} mb={3}> */}
          <Grid container spacing={1} display={"flex"} alignItems={"center"}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <MDTypography variant="h5" fontWeight="medium">
                Attendance Details
              </MDTypography>
            </Grid>
          </Grid>
          {/* </MDBox> */}
        </MDBox>
        <MDBox mb={2} />
      </Card>
      <Card id="delete-account" sx={{ mb: 4 }}>
        <MDBox p={2}>
          <Grid container spacing={3}>
            <>
              <Grid item size={{ xs: 12, md: 6 }}>
                <MDAutocomplete
                  label="Users"
                  name="user_name"
                  value={filters.user_name}
                  options={usersData}
                  onChange={(newValue) => {
                    setFilters({ ...filters, user_name: newValue });
                  }}
                  isFilter={true} // optional: add a flag if you need special styling
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <MDAutocomplete
                  label="District"
                  name="district_name"
                  value={filters.district_name}
                  options={districtData}
                  onChange={(newValue) => {
                    setFilters({ ...filters, district_name: newValue });
                  }}
                  isFilter={true} // optional: add a flag if you need special styling
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <MDAutocomplete
                  label="Status"
                  name="status"
                  value={filters.status}
                  options={attendanceOptions}
                  onChange={(newValue) => {
                    setFilters({ ...filters, status: newValue });
                  }}
                  isFilter={true} // optional: add a flag if you need special styling
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Box sx={{ width: "100%" }}>
                  <Typography sx={{ fontSize: "0.887rem", fontWeight: 400, mb: 0.3 }}>
                    Select Date Range
                  </Typography>
                  <TextField
                    fullWidth
                    value={
                      filters.start_date && filters.end_date
                        ? `${filters.start_date} to ${filters.end_date}`
                        : ""
                    }
                    placeholder="Select Date Range"
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget);
                      setOpenCalendar(true);
                    }}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        cursor: "pointer",
                        backgroundColor: "#fff",
                        height: "51px",
                      },
                    }}
                  />
                  <Popper
                    open={openCalendar}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    style={{ zIndex: 1300 }}
                  >
                    <ClickAwayListener onClickAway={() => setOpenCalendar(false)}>
                      <Box
                        sx={{
                          mt: 1,
                          bgcolor: "white",
                          boxShadow: 3,
                          borderRadius: 2,
                          p: 2,
                          zIndex: 1301,
                        }}
                      >
                        <DateRange
                          editableDateInputs={true}
                          onChange={(item) => {
                            setSelectedDateRange([item.selection]);
                            const start = item.selection.startDate;
                            const end = item.selection.endDate;
                            setFilters({
                              ...filters,
                              start_date: start ? format(start, "dd-MM-yyyy") : "",
                              end_date: end ? format(end, "dd-MM-yyyy") : "",
                            });
                            if (start && end && start.getTime() !== end.getTime()) {
                              setOpenCalendar(false);
                            }
                          }}
                          moveRangeOnFirstSelection={false}
                          ranges={selectedDateRange}
                          rangeColors={["#1976d2"]}
                          maxDate={new Date()}
                          locale={enUS}
                        />
                      </Box>
                    </ClickAwayListener>
                  </Popper>
                </Box>
              </Grid>
            </>
          </Grid>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={2} justifyContent="center">
            {/* Search Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                onClick={() => {
                  setShowNote(false);
                  setTimeout(() => {
                    console.log("✅ Using latest filters:", filters);
                    fetchAttendanceData(0, pagination.pageSize, filters, sorting);
                  }, 0);
                }}
                sx={{
                  color: "white !important",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#155fa0",
                  },
                  minWidth: "120px",
                }}
              >
                Search
              </MDButton>
            </Grid>

            {/* Cancel Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                variant="outlined"
                onClick={() => {
                  const cleared = {
                    user_name: "",
                    district_name: "",
                    start_date: null,
                    end_date: null,
                    status: "",
                    date_range: null,
                  };
                  setFilters(cleared);
                  setShowNote(true);
                  setSelectedDateRange([
                    {
                      startDate: new Date(),
                      endDate: new Date(),
                      key: "selection",
                    },
                  ]);
                  fetchAttendanceData(0, pagination.pageSize, cleared);
                }}
                sx={{
                  color: "#b71c1c !important",
                  borderColor: "#b71c1c",
                  "&:hover": {
                    backgroundColor: "#fbe9e7",
                    borderColor: "#b71c1c",
                  },
                  minWidth: "120px",
                }}
              >
                Cancel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
      <Box>
        <Grid container spacing={5}>
          <Grid item size={{ xs: 12, md: 12 }} className="listTable">
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Paper
                elevation={2}
                sx={{
                  width: "100%",
                  overflowX: "auto",
                  backgroundColor: "#fff",
                  boxShadow: "none",
                  p: 2,
                }}
              >
                {showNote && start_date && end_date && (
                  <span
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginLeft: "10px",
                    }}
                  >
                    (Note: * This Attendance data is from{" "}
                    {format(new Date(start_date.split("-").reverse().join("-")), "MMMM dd, yyyy")}{" "}
                    to {format(new Date(end_date.split("-").reverse().join("-")), "MMMM dd, yyyy")})
                  </span>
                )}
                <MaterialReactTable
                  columns={columns}
                  data={attendanceData}
                  // getRowId={(row) => row.id}
                  enableGlobalFilter={false}
                  enableFullScreenToggle={false}
                  enableDensityToggle={true}
                  initialState={{ showColumnFilters: false }}
                  layoutMode="grid"
                  enableStickyHeader
                  enableRowNumbers
                  manualFiltering
                  manualPagination
                  manualSorting
                  onPaginationChange={setPagination}
                  onSortingChange={setSorting}
                  rowCount={rowCount}
                  onColumnFiltersChange={(filters) => {
                    setColumnFilters(filters);
                    fetchAttendanceData(
                      pagination.pageIndex,
                      pagination.pageSize,
                      filters,
                      sorting,
                      filters
                    );
                  }}
                  state={{
                    columnFilters,
                    // globalFilter,
                    // isLoading,
                    loading,
                    pagination,
                    // showAlertBanner: isError,
                    // showProgressBars: isRefetching,
                    sorting,
                  }}
                  muiTableHeadProps={{
                    sx: {
                      padding: "0px", // won't apply directly to <thead>, but useful for general structure
                      backgroundColor: "#011F3A", // background on <thead> element itself
                    },
                  }}
                  enableColumnActions={false}
                  muiTableHeadCellProps={{
                    sx: {
                      backgroundColor: "#004030",
                      color: "#fff",
                      // fontSize: '16px',
                      fontWeight: "bold",
                      position: "sticky", // Make header sticky
                      top: 0, // Stick it at the top
                      zIndex: 1, // Ensure it stays
                      "& .MuiTableSortLabel-root": {
                        "& .MuiTableSortLabel-icon": {
                          color: "#fff !important",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                        fontSize: "12px",
                        "::placeholder": {
                          color: "#e0e0e0", // Default (blurred) placeholder color
                          opacity: 1,
                        },
                      },
                      "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                        color: "#ffffff", // Focused placeholder color
                      },
                      "& .MuiIconButton-root": {
                        color: "#fff",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#e0e0e0",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff",
                      },

                      "& .MuiCheckbox-root": {
                        color: "#fff",
                      },
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      padding: "8px", // reduce from default 16px
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  enableExpandAll={false}
                  enableExpanding
                  getRowCanExpand={(row) => row.original.status === "Leave"}
                  renderDetailPanel={({ row }) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "16px",
                        fontSize: "14px",
                        backgroundColor: "#f3f3f3",
                        borderLeft: "4px solid #ff9800",
                        lineHeight: 1.6,
                      }}
                    >
                      <Typography>
                        <strong>Reason:</strong> {row.original.reason || "N/A"}
                      </Typography>
                    </Box>
                  )}
                  muiTableBodyRowProps={({ row }) => ({
                    sx: {
                      backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0", // soft alternating colors
                    },
                  })}
                  muiExpandButtonProps={({ row }) => ({
                    sx: { marginLeft: "-10px" }, // reduce spacing
                    title: "", // remove tooltip
                  })}
                  muiExpandAllButtonProps={{
                    sx: {
                      visibility: "hidden", // hides expand header cell
                      width: "10px",
                    },
                  }}
                  displayColumnDefOptions={{
                    "mrt-row-numbers": {
                      size: 40,
                      maxSize: 40,
                      position: "left", // ensure it's on the left
                    },
                    "mrt-row-expand": {
                      size: 20,
                      maxSize: 20,
                    },
                  }}
                  renderTopToolbarCustomActions={({ table }) => (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        p: "0.5rem",
                        flexWrap: "wrap",
                        marginLeft: "auto",
                      }}
                    >
                      <Tooltip title={"Export data to excel sheet"} arrow>
                        <Button
                          onClick={handleExportData}
                          sx={{
                            background:
                              "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                            color: "#f4f4f4",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            transition: "background 0.3s, transform 0.3s",
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%))",
                              transform: "scale(1.05)",
                            },
                            "&:active": {
                              transform: "scale(0.95)",
                            },
                          }}
                          startIcon={<IconTableExport />}
                        >
                          {"Export To Excel"}
                        </Button>
                      </Tooltip>
                    </Box>
                  )}
                  muiTopToolbarProps={{
                    sx: {
                      backgroundColor: "#fff",
                      boxShadow: "none",
                    },
                  }}
                />
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={Boolean(message.status)}
        autoHideDuration={3000}
        onClose={() => setMessage({ status: "", title: "", msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={message.status}
          onClose={() => setMessage({ status: "", title: "", msg: "" })}
          variant="filled"
        >
          {message.msg}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
};

export default AttendanceDetailsPage;
